import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import {
  getBulkUpdateTasksUrl,
  getBulkUpdateTasksRequestBody,
  deserializeBulkUpdateTasksResponse,
  trackBulkAssignEvent,
} from './utils';
import { selectProjectTasksTableFilters } from '../../projectTasksListSlice';
import {
  selectFilteredSelectedTasksIds,
  selectProjectTasksExportState,
} from '../../export/exportSlice';
import { TaskDetailToUpdate } from 'types/store/taskDetail';
import {
  setBulkActionsNotifications,
  setBulkOperationInProgress,
  setBulkActionOperationIdAndType,
} from '../bulkActionsSlice';
import {
  bulkUpdateTasks as bulkUpdateTasksApi,
  BulkUpdateTasksResponse,
} from 'api/tasksList';
import {
  generateBulkAssignNotifications,
  generateRejectedBulkAssignNotifications,
} from '../notifications';

interface BulkAssignState {
  status?: Status;
  error?: string;
}

const initialState: BulkAssignState = {};

export interface BulkUpdateTasksAssigneesParams {
  task: Partial<TaskDetailToUpdate>;
  projectId: string;
  config?: AxiosRequestConfig;
  canUndo?: boolean;
}

function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().projectTasksList.bulkActions.bulkAssign.value;
  return status !== SLICE_STATUS.LOADING;
}

export const bulkUpdateTasksAssignees = createAsyncThunk<
  BulkUpdateTasksResponse,
  BulkUpdateTasksAssigneesParams,
  ThunkApi
>(
  'projectTasksList/bulkActions/bulkAssign/bulkUpdateTasksAssignees',
  async (
    params: BulkUpdateTasksAssigneesParams,
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(setBulkOperationInProgress(true));
      const state = getState() as RootState;
      const filters = selectProjectTasksTableFilters(state);
      const exportState = selectProjectTasksExportState(state);
      const filteredSelectedTasksIds = selectFilteredSelectedTasksIds(state);
      const url = getBulkUpdateTasksUrl({
        filters,
        exportState: {
          selectAll: exportState.selectAllTasks,
          excludedIds: exportState.excludedTasksIds,
          selectedIds: filteredSelectedTasksIds,
        },
        projectId: params.projectId,
      });
      const requestBody = getBulkUpdateTasksRequestBody(params.task);
      const response = await bulkUpdateTasksApi(
        url,
        requestBody,
        params.config
      );
      const deserializedResponse = await deserializeBulkUpdateTasksResponse(
        response
      );
      trackBulkAssignEvent(state, deserializedResponse);
      dispatch(
        setBulkActionOperationIdAndType({
          id: deserializedResponse.operationId,
          type: 'update',
        })
      );
      const notifications = generateBulkAssignNotifications(
        deserializedResponse.successfulCount,
        deserializedResponse.failedCount,
        params.canUndo
      );
      dispatch(setBulkOperationInProgress(false));
      dispatch(setBulkActionsNotifications(notifications));
      return deserializedResponse;
    } catch (error) {
      const notifications = generateRejectedBulkAssignNotifications();
      dispatch(setBulkActionsNotifications(notifications));
      return rejectWithValue(error);
    }
  },
  {
    condition: concurrencyCondition,
  }
);

const bulkAssignSlice = createSlice({
  name: 'projectTasksList/bulkActions/bulkAssign',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(bulkUpdateTasksAssignees.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(bulkUpdateTasksAssignees.fulfilled, (state, action) => {
        state.status = SLICE_STATUS.SUCCESS;
        state.error = undefined;
      })
      .addCase(bulkUpdateTasksAssignees.rejected, (state, action) => {
        state.status = SLICE_STATUS.FAILED;
        state.error = action.error.message;
      });
  },
});

const selectBulkAssignState = (state: RootState) =>
  state.projectTasksList.bulkActions.bulkAssign.value;

const selectBulkAssignStatus = createSelector(
  [selectBulkAssignState],
  (bulkAssignState) => bulkAssignState.status
);

export const selectIsBulkAssignLoading = createSelector(
  [selectBulkAssignStatus],
  (status) => status === SLICE_STATUS.LOADING
);

export default bulkAssignSlice.reducer;
